import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    productInterest: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch('https://formspree.io/f/xzdjlypy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Message sent! Our team will contact you shortly.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          productInterest: ''
        });
      } else {
        toast.error('Oops! Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('Oops! Something went wrong. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <section className="bg-slate-900 py-24 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Contact Us</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Get in touch for product information, specifications, and project consultation
          </p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 to-transparent pointer-events-none -z-10"></div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Get in Touch</h2>
                <p className="text-slate-600 leading-relaxed">
                  Contact us for product information, specifications, and assistance with product selection for your project requirements.
                </p>
              </div>

              <div className="space-y-6">
                <ContactItem icon={<Mail className="w-6 h-6 text-white" />} title="Email" value={companyInfo.contact.email} href={`mailto:${companyInfo.contact.email}`} />
                <ContactItem icon={<Phone className="w-6 h-6 text-white" />} title="Phone" value={companyInfo.contact.phone} href={`tel:${companyInfo.contact.phone}`} />
                <ContactItem icon={<MapPin className="w-6 h-6 text-white" />} title="Location" value={companyInfo.contact.address} />
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 transform transition-transform hover:scale-105">
                <h2 className="text-3xl font-bold text-slate-900 mb-8">Send us a Message</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField label="Full Name *" name="name" type="text" value={formData.name} onChange={handleChange} required placeholder="John Smith" />
                    <InputField label="Email Address *" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="john@company.co.nz" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InputField label="Phone Number" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+64 9 XXX XXXX" />
                    <InputField label="Company Name" name="company" type="text" value={formData.company} onChange={handleChange} placeholder="Company Ltd" />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Product Interest</label>
                    <select
                      name="productInterest"
                      value={formData.productInterest}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                    >
                      <option value="">Select a category</option>
                      <option value="paints">Paints</option>
                      <option value="protective-finishes">Protective Finishes</option>
                      <option value="primers-protective-paints">Primers & Protective Paints</option>
                      <option value="specialty-surfaces">Specialty Surfaces</option>
                      <option value="general">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all resize-none"
                      placeholder="Tell us about your project requirements..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center px-8 py-4 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-semibold text-lg group"
                  >
                    <Send className="mr-2 w-5 h-5" />
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Reusable components
const InputField = ({ label, name, type, value, onChange, required, placeholder }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-900 mb-2">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
    />
  </div>
);

const ContactItem = ({ icon, title, value, href }) => (
  <div className="flex items-start space-x-4">
    <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center flex-shrink-0">{icon}</div>
    <div>
      <h3 className="font-semibold text-slate-900 mb-1">{title}</h3>
      {href ? (
        <a href={href} className="text-slate-600 hover:text-slate-900">{value}</a>
      ) : (
        <p className="text-slate-600">{value}</p>
      )}
    </div>
  </div>
);

export default Contact;
